import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconeFechar } from './icones';

/**
 * O diálogo do painel.
 *
 * O `confirm()` do navegador foi embora daqui por três motivos concretos: ele
 * aparece colado no topo da janela com a tipografia do sistema, ignorando o
 * tema escuro da tela inteira; no celular ele rouba a tela toda; e ele bloqueia
 * a linha de execução, o que congela a conversa ao vivo enquanto a pessoa lê a
 * pergunta.
 *
 * Feito à mão, sem dependência nova: `<dialog>` nativo traria o mesmo problema
 * de estilo em cada navegador, e uma biblioteca de diálogo custa mais do que
 * estas quarenta linhas.
 */
export function Modal({
  titulo,
  aberto,
  aoFechar,
  children,
  rodape,
}: {
  titulo: string;
  aberto: boolean;
  aoFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
}): React.JSX.Element | null {
  const caixa = useRef<HTMLDivElement>(null);
  const devolverFoco = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!aberto) return;
    // Quem estava com o foco volta a tê-lo quando o diálogo fecha: sem isso,
    // fechar joga o foco no começo da página e quem navega por teclado perde o
    // lugar onde estava.
    devolverFoco.current = document.activeElement as HTMLElement | null;
    const primeiro = caixa.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    primeiro?.focus();
    return () => devolverFoco.current?.focus?.();
  }, [aberto]);

  const aoTeclar = useCallback(
    (evento: React.KeyboardEvent<HTMLDivElement>): void => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        aoFechar();
        return;
      }
      if (evento.key !== 'Tab') return;
      // Prender o foco dentro do diálogo: com ele aberto, o resto da tela não
      // está disponível, e sair com Tab levaria a botões que não podem ser
      // usados agora.
      const focaveis = [
        ...(caixa.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ];
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (!primeiro || !ultimo) return;
      if (evento.shiftKey && document.activeElement === primeiro) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    },
    [aoFechar],
  );

  if (!aberto) return null;

  /**
   * Por um portal no `body`: dentro da árvore, o diálogo herdaria o
   * `overflow: hidden` e o empilhamento dos painéis do workspace, e apareceria
   * cortado pela metade — que foi exatamente o tipo de defeito que os ensaios
   * de tela existem para pegar.
   */
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={aoFechar}
      onKeyDown={aoTeclar}
      role="presentation"
    >
      <div
        ref={caixa}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h2 className="font-medium text-zinc-100">{titulo}</h2>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            title="Fechar"
            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-200"
          >
            <IconeFechar />
          </button>
        </div>
        <div className="px-5 py-4 text-sm text-zinc-300">{children}</div>
        {rodape ? (
          <div className="flex justify-end gap-2 border-t border-zinc-800 px-5 py-3">{rodape}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Perguntar antes de fazer algo que não tem volta.
 *
 * `perigosa` pinta o botão de vermelho: remover uma máquina e salvar um nome
 * novo não podem parecer a mesma decisão.
 */
export function Confirmacao({
  titulo,
  aberto,
  texto,
  rotuloConfirmar = 'Confirmar',
  perigosa = false,
  aoConfirmar,
  aoCancelar,
}: {
  titulo: string;
  aberto: boolean;
  texto: ReactNode;
  rotuloConfirmar?: string;
  perigosa?: boolean;
  aoConfirmar: () => void;
  aoCancelar: () => void;
}): React.JSX.Element | null {
  return (
    <Modal
      titulo={titulo}
      aberto={aberto}
      aoFechar={aoCancelar}
      rodape={
        <>
          <button
            type="button"
            onClick={aoCancelar}
            className="rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 transition hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={aoConfirmar}
            className={
              'rounded-md px-3 py-1.5 text-xs font-medium text-white transition ' +
              (perigosa ? 'bg-red-600 hover:bg-red-500' : 'bg-sky-600 hover:bg-sky-500')
            }
          >
            {rotuloConfirmar}
          </button>
        </>
      }
    >
      {texto}
    </Modal>
  );
}

/**
 * O par pronto para trocar um `confirm()` por um diálogo, sem cada tela ter de
 * inventar o próprio estado.
 *
 * Uso: `const [pedir, dialogo] = usarConfirmacao();` — `pedir({...})` abre e
 * `dialogo` é o que se põe na árvore.
 */
export type PedidoDeConfirmacao = {
  titulo: string;
  texto: ReactNode;
  rotuloConfirmar?: string;
  perigosa?: boolean;
  aoConfirmar: () => void;
};

export function usarConfirmacao(): [
  (pedido: PedidoDeConfirmacao) => void,
  React.JSX.Element | null,
] {
  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);

  const dialogo = pedido ? (
    <Confirmacao
      aberto
      titulo={pedido.titulo}
      texto={pedido.texto}
      rotuloConfirmar={pedido.rotuloConfirmar}
      perigosa={pedido.perigosa}
      aoCancelar={() => setPedido(null)}
      aoConfirmar={() => {
        // Fecha antes de agir: a ação costuma navegar ou recarregar a lista, e
        // um diálogo ainda aberto por cima disso fica órfão na tela.
        setPedido(null);
        pedido.aoConfirmar();
      }}
    />
  ) : null;

  return [setPedido, dialogo];
}
